import {
  getLeoUuid,
  LeoUUID,
  getList,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SingleChoiceOption, SingleChoiceOptionDTO } from "../types/singleChoiceOption";
/* eslint-disable import/export */
export abstract class ValidateCSVAndUpdateQuestionOptionsRPC implements
  LeoRPC<ValidateCSVAndUpdateQuestionOptionsRPC.Request, ValidateCSVAndUpdateQuestionOptionsRPC.Response, ValidateCSVAndUpdateQuestionOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: ValidateCSVAndUpdateQuestionOptionsRPC.Request
  ): Promise<LeoRPCResult<ValidateCSVAndUpdateQuestionOptionsRPC.Response, ValidateCSVAndUpdateQuestionOptionsRPC.Errors.Errors>>;
}

export namespace ValidateCSVAndUpdateQuestionOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    questionId: string;
    documentId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isValidateCSVAndUpdateQuestionOptionsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly questionId: LeoUUID;
    readonly documentId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    questionId: LeoUUID, 
    documentId: LeoUUID
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.questionId = questionId;
      this.documentId = documentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      const documentId = getLeoUuid(dto, "documentId", InvalidRequestDocumentIdError);
      return new Request(
        projectId,
        surveyId,
        questionId,
        documentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          questionId: this.questionId.toDTO(),
          documentId: this.documentId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ValidateCSVAndUpdateQuestionOptionsRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',questionId='${this.questionId}',documentId='${this.documentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    singleChoiceOptions: SingleChoiceOptionDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isValidateCSVAndUpdateQuestionOptionsResponse: boolean = true;
  
    readonly singleChoiceOptions: SingleChoiceOption[];
  
  
    constructor(
    singleChoiceOptions: SingleChoiceOption[]
    ) {
      this.singleChoiceOptions = singleChoiceOptions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const singleChoiceOptions = getList<SingleChoiceOptionDTO, SingleChoiceOption>(
        dto,
        "singleChoiceOptions",
        SingleChoiceOption.fromDTO,
        InvalidResponseSingleChoiceOptionsError
      );
      return new Response(
        singleChoiceOptions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          singleChoiceOptions: this.singleChoiceOptions.map((e: SingleChoiceOption) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `ValidateCSVAndUpdateQuestionOptions(singleChoiceOptions='${this.singleChoiceOptions}')`;
  };
  

  export enum RPCError {
    InvalidFile = "INVALID_FILE",
    UnknownFile = "UNKNOWN_FILE",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    QuestionWasDeleted = "QUESTION_WAS_DELETED",
    IncorrectQuestionType = "INCORRECT_QUESTION_TYPE",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyIsLive = "SURVEY_IS_LIVE"
  }

  export namespace Errors {
    
    export interface InvalidFileDTO {
      code: string;
      invalidFileDetails: string;
    }
    
    export class InvalidFile extends LeoRPCError<RPCError> {
      readonly isInvalidFile: boolean = true;
    
      readonly invalidFileDetails: string;
    
      constructor(
      invalidFileDetails: string
      ) {
        const code = RPCError.InvalidFile;
        super(code);
        InvalidFile.validateInvalidFileDetails(invalidFileDetails);
        this.invalidFileDetails = invalidFileDetails;
      }
    
      static fromDTO(dto: InvalidFileDTO): InvalidFile {
        const invalidFileDetails = getString(dto, "invalidFileDetails", InvalidResponseInvalidFileInvalidFileDetailsError);
        return new InvalidFile(
          invalidFileDetails
        );
      }
    
      toDTO(): InvalidFileDTO {
        const dto: InvalidFileDTO = {
          code: this.code,
          invalidFileDetails: this.invalidFileDetails
        };
        return dto;
      }
    
      private static validateInvalidFileDetails = (invalidFileDetails: string): void => {
        if(invalidFileDetails.trim().length === 0) {
          throw new InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError(
            `Attribute invalidFileDetails cannot be an empty string.`
          );
        }
      }
      
    }
    
    InvalidFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.INVALID_FILE(invalidFileDetails='${this.invalidFileDetails}')`;
    };
    
    
    export interface UnknownFileDTO {
      code: string;
    }
    
    export class UnknownFile extends LeoRPCError<RPCError> {
      readonly isUnknownFile: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnknownFile;
        super(code);
      }
    
    
      toDTO(): UnknownFileDTO {
        const dto: UnknownFileDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnknownFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.UNKNOWN_FILE()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface QuestionWasDeletedDTO {
      code: string;
    }
    
    export class QuestionWasDeleted extends LeoRPCError<RPCError> {
      readonly isQuestionWasDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionWasDeleted;
        super(code);
      }
    
    
      toDTO(): QuestionWasDeletedDTO {
        const dto: QuestionWasDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionWasDeleted.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.QUESTION_WAS_DELETED()`;
    };
    
    
    export interface IncorrectQuestionTypeDTO {
      code: string;
    }
    
    export class IncorrectQuestionType extends LeoRPCError<RPCError> {
      readonly isIncorrectQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.IncorrectQuestionType;
        super(code);
      }
    
    
      toDTO(): IncorrectQuestionTypeDTO {
        const dto: IncorrectQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    IncorrectQuestionType.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.INCORRECT_QUESTION_TYPE()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface SurveyIsLiveDTO {
      code: string;
    }
    
    export class SurveyIsLive extends LeoRPCError<RPCError> {
      readonly isSurveyIsLive: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsLive;
        super(code);
      }
    
    
      toDTO(): SurveyIsLiveDTO {
        const dto: SurveyIsLiveDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsLive.prototype.toString = function toString(): string {
      return `ValidateCSVAndUpdateQuestionOptionsRPC.SURVEY_IS_LIVE()`;
    };
    

    export type Errors = InvalidFile | UnknownFile | InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived | InvalidQuestionId | QuestionWasDeleted | IncorrectQuestionType | SurveyIsClosed | SurveyIsLive;
    export type ErrorsDTO = InvalidFileDTO | UnknownFileDTO | InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | InvalidQuestionIdDTO | QuestionWasDeletedDTO | IncorrectQuestionTypeDTO | SurveyIsClosedDTO | SurveyIsLiveDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFile:
          return InvalidFile.fromDTO(dto as InvalidFileDTO);
        case RPCError.UnknownFile:
          return new UnknownFile();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.QuestionWasDeleted:
          return new QuestionWasDeleted();
        case RPCError.IncorrectQuestionType:
          return new IncorrectQuestionType();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.SurveyIsLive:
          return new SurveyIsLive();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestQuestionIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidRequestDocumentIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDocumentIdError";
  }
}

export class InvalidResponseSingleChoiceOptionsError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSingleChoiceOptionsError";
  }
}

export class InvalidResponseInvalidFileError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsError extends InvalidResponseInvalidFileError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError extends InvalidResponseInvalidFileInvalidFileDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError";
  }
}
export class InvalidResponseUnknownFileError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnknownFileError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseQuestionWasDeletedError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionWasDeletedError";
  }
}

export class InvalidResponseIncorrectQuestionTypeError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIncorrectQuestionTypeError";
  }
}

export class InvalidResponseSurveyIsClosedError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsLiveError extends ValidateCSVAndUpdateQuestionOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsLiveError";
  }
}

